<template>
  <ValidationObserver ref="observer" data-testid="demographic-step">
    <OnboardingModalContent
      :title="$t('onboarding_demographic_subtitle')"
      :error="formError"
    >
      <div
        class="
          tw-grid
          xl:tw-grid-cols-2 xl:tw-grid-rows-2 xl:tw-gap-x-7 xl:tw-pt-8
        "
      >
        <OnboardingField
          v-slot="{ errors }"
          :label="$t('onboarding_first_name')"
          for-id="first_name"
          validation-rules="required|max:50|alpha_common_puntuation_marks"
        >
          <OnboardingInput
            id="first_name"
            v-model="demographicData.firstName"
            name="first_name"
            :class="{
              'tw-border': errors.length > 0,
              'tw-border-[#EE2828]': errors.length > 0,
              'tw-w-full': true,
            }"
          />
        </OnboardingField>
        <OnboardingField
          v-slot="{ errors }"
          :label="$t('onboarding_last_name')"
          for-id="last_name"
          validation-rules="required|max:50|alpha_common_puntuation_marks"
        >
          <OnboardingInput
            id="last_name"
            v-model="demographicData.lastName"
            name="last_name"
            :class="{
              'tw-border': errors.length > 0,
              'tw-border-[#EE2828]': errors.length > 0,
              'tw-w-full': true,
            }"
          />
        </OnboardingField>
        <OnboardingField
          v-slot="{ errors }"
          :label="$t('onboarding_dob')"
          for-id="dob"
          validation-rules="required|minimum_age"
        >
          <OnboardingInput
            id="dob"
            v-model="demographicData.dob"
            name="dob"
            placeholder="    /    /  "
            mask="##/##/####"
            :masked="true"
            :class="{
              'tw-border': errors.length > 0,
              'tw-border-[#EE2828]': errors.length > 0,
              'tw-w-full': true,
            }"
          />
        </OnboardingField>
      </div>
      <template #bottom>
        <div>
          <div class="tw-flex tw-justify-center tw-my-4">
            <Button type="submit" @click="updateUserProfile">{{
              $t('onboarding_save')
            }}</Button>
          </div>
        </div>
      </template>
    </OnboardingModalContent>
  </ValidationObserver>
</template>

<script>
import { mapState } from 'vuex'
import { parse, format, parseISO, isValid } from 'date-fns'
import { ValidationObserver } from 'vee-validate'
import OnboardingModalContent from './OnboardingModalContent'
import OnboardingField from '~/components/onboarding/OnboardingField'
import Button from '@/components/onboarding/Button'
import OnboardingInput from '~/components/onboarding/OnboardingInput'
import OnboardingMixin from '@/components/onboarding/OnboardingMixin'
import { formatDob } from '@/utils/individual'

export default {
  components: {
    OnboardingInput,
    OnboardingField,
    Button,
    OnboardingModalContent,
    ValidationObserver,
  },
  mixins: [OnboardingMixin],
  data() {
    return {
      demographicData: {
        firstName: '',
        lastName: '',
        dob: '',
      },
      formError: '',
    }
  },
  computed: {
    ...mapState('onboarding', ['demographicDataStored']),
    ...mapState('fiiAuth', ['identity']),
    cacheDemographicData() {
      const { firstName, lastName, dob } = this.identity?.meta ?? {}
      const parsedDob = parseISO(dob)

      return (
        this.demographicDataStored ?? {
          firstName: firstName ?? '',
          lastName: lastName ?? '',
          dob: isValid(parsedDob) ? format(parsedDob, 'MM/dd/yyyy') : '',
        }
      )
    },
  },
  mounted() {
    this.demographicData = this.cacheDemographicData
  },
  methods: {
    async updateUserProfile() {
      const { isValid, fields } = await this.$refs.observer.validateWithInfo()
      if (!isValid) {
        const failedRules = Object.values(fields)
          .filter((field) => !field.valid)
          .map((field) => Object.keys(field.failedRules))
          .flat()
        const customValidationErrors = {
          required: this.$t('onboarding_validation_error_msg_required'),
          alpha_common_puntuation_marks: this.$t(
            'onboarding_validation_error_msg_invalid'
          ),
          minimum_age: this.$t('onboarding_validation_error_msg_min_age'),
        }
        this.formError = customValidationErrors[failedRules[0]]
      } else {
        this.formError = ''
        const date = parse(this.demographicData.dob, 'MM/dd/yyyy', new Date())
        this.$emit('saving')
        this.$store.commit(
          'onboarding/setDemographicData',
          this.demographicData
        )

        await this.$axios
          .patch('/AUTH_URL/user', {
            first_name: this.demographicData.firstName,
            last_name: this.demographicData.lastName,
            dob: formatDob(date),
            onboarded: true,
            // TODO: Missing TOS date
          })
          .then((r) => {
            this.$emit('next')
          })
      }
    },

    getErrorMessage(errors) {
      console.log(errors)
    },
  },
}
</script>

<style></style>
